/**
 * The app-catalog vocabulary (APP-ENTITLEMENT-001): the closed sets every catalog row, every entitlement row and
 * every surface shares. Data only; requires nothing.
 *
 * Every value here is also a database CHECK (pos-api `app_entitlement_001_*` migrations). A new value is a
 * migration AND a change here, never one without the other — the migration tests pin the CHECK text to these lists.
 *
 * @module @puffin-core/app-catalog/vocabulary
 */

'use strict';

/** What a catalog row IS: the product a business buys, or an app that attaches to one. */
const APP_KIND = Object.freeze({
  ANCHOR: 'anchor',
  APP: 'app',
});

const APP_KIND_VALUES = Object.freeze(Object.values(APP_KIND));

/**
 * How an app is paid for (owner rulings 2026-10-10, Q-AE-14 / Q-AE-16 / Q-AE-23):
 * - `per_unit` — a price per unit of each of its size dimensions (a location, a line, a mailbox …);
 * - `included_with_anchor` — free with any anchor, called out as free (PalmPuffin, PrepPuffin);
 * - `share_of_recovery` — a share of what it recovers and no monthly fee (PatrolPuffin, 20 %).
 */
const PRICING_MODEL = Object.freeze({
  PER_UNIT: 'per_unit',
  INCLUDED_WITH_ANCHOR: 'included_with_anchor',
  SHARE_OF_RECOVERY: 'share_of_recovery',
});

const PRICING_MODEL_VALUES = Object.freeze(Object.values(PRICING_MODEL));

/**
 * The size dimensions an app may scale on. An owner must be able to count a dimension on the floor (Q-AE-3): a
 * location, a register, a site, a line, a mailbox, a storefront, an employee paid this month, an office, a
 * technician — never a user or a seat. `aiReceptionist` is counted per location it is switched on for.
 */
const DIMENSION_KEY = Object.freeze({
  // The account itself — exactly one, never counted: the flat part of a price (PayDayPuffin's $49.95 base, Q-AE-14).
  ACCOUNT: 'account',
  LOCATION: 'location',
  REGISTER: 'register',
  SITE: 'site',
  LINE: 'line',
  AI_RECEPTIONIST: 'aiReceptionist',
  MAILBOX: 'mailbox',
  EMPLOYEE_PAID: 'employeePaid',
  STOREFRONT: 'storefront',
  OFFICE: 'office',
  TECHNICIAN: 'technician',
});

const DIMENSION_KEY_VALUES = Object.freeze(Object.values(DIMENSION_KEY));

/**
 * The consumables an app meters inside a period. Each is INCLUDED up to a quantity, then sold in whole blocks under
 * a cap the store sets (Q-AE-9: every third-party cost capped per period; no overage, ever). `aiOutcomes` is a REAL
 * result of PortPuffin's AI receptionist — a booking confirmed or rescheduled, a package found (owner rulings
 * Q-AE-57 / Q-AE-58, OUTCOME-SKU-CONSUMABLE-001); a lookup that found nothing is never one.
 */
const CONSUMABLE_KEY = Object.freeze({
  BLOG_POSTS: 'blogPosts',
  AI_MINUTES: 'aiMinutes',
  SMS_SEGMENTS: 'smsSegments',
  AI_GENERATIONS: 'aiGenerations',
  AI_QUALIFICATIONS: 'aiQualifications',
  AI_OUTCOMES: 'aiOutcomes',
});

const CONSUMABLE_KEY_VALUES = Object.freeze(Object.values(CONSUMABLE_KEY));

/**
 * Where a dimension's quantity comes from. `derived` — counted from the rows the account really has (its locations,
 * its registers, its provisioned numbers); `declared` — the owner switches it on per parent unit (the AI
 * receptionist per location).
 */
const QUANTITY_SOURCE = Object.freeze({
  DERIVED: 'derived',
  DECLARED: 'declared',
});

const QUANTITY_SOURCE_VALUES = Object.freeze(Object.values(QUANTITY_SOURCE));

/**
 * What kind of row a catalog dimension is (`app_catalog_dimensions."dimensionKind"`, and every public row's
 * `dimensionKind`): a SIZE the account scales on and pays per unit of, or a CONSUMABLE metered inside a period and
 * sold in blocks beyond its inclusion. One vocabulary for the seed, the console, the public catalog, the Billing
 * page, the signup basket and the marketing pricing section (APP-ENTITLEMENT-001 Phase 11).
 */
const DIMENSION_KIND = Object.freeze({
  SIZE: 'size',
  CONSUMABLE: 'consumable',
});

const DIMENSION_KIND_VALUES = Object.freeze(Object.values(DIMENSION_KIND));

/**
 * A consumable's scope: included once per account, or once per unit of a parent dimension (150 AI minutes per
 * location that has the receptionist; 250 SMS segments per line, pooled across the account's lines).
 */
const CONSUMABLE_SCOPE = Object.freeze({
  ACCOUNT: 'account',
  PER_PARENT: 'per_parent',
});

const CONSUMABLE_SCOPE_VALUES = Object.freeze(Object.values(CONSUMABLE_SCOPE));

/**
 * An entitlement's status (`tenant_app_entitlements."status"`, Q-AE-19): a 14-day trial, active, read-only (the
 * trial ended with no card and no confirmation — nothing deleted, nothing charged), or off.
 */
const ENTITLEMENT_STATUS = Object.freeze({
  TRIALING: 'trialing',
  ACTIVE: 'active',
  READ_ONLY: 'read_only',
  OFF: 'off',
});

const ENTITLEMENT_STATUS_VALUES = Object.freeze(Object.values(ENTITLEMENT_STATUS));

/** The statuses under which an account HOLDS an app (may reach it, read-only or not). */
const HELD_ENTITLEMENT_STATUSES = Object.freeze([
  ENTITLEMENT_STATUS.TRIALING,
  ENTITLEMENT_STATUS.ACTIVE,
  ENTITLEMENT_STATUS.READ_ONLY,
]);

/**
 * Entitlement status → badge TONE (`<StatusBadgeCell statusTones={…}>`, Q-MI-25), one entry per status. The values
 * are the closed tone set's own strings, spelled literally because this package requires nothing
 * (`@puffin-core/status-taxonomy` `BADGE_TONE_VALUES` is what the tests judge them against).
 */
const ENTITLEMENT_STATUS_BADGE_TONES = Object.freeze({
  [ENTITLEMENT_STATUS.TRIALING]: 'info',
  [ENTITLEMENT_STATUS.ACTIVE]: 'success',
  [ENTITLEMENT_STATUS.READ_ONLY]: 'warning',
  [ENTITLEMENT_STATUS.OFF]: 'neutral',
});

/**
 * Why an account holds an app: it ordered it, it came included with the anchor, or the platform granted it
 * (`complimentary` — no card, no charge, no trial end; every billing path decides on this value explicitly).
 */
const ENTITLEMENT_SOURCE = Object.freeze({
  ORDER: 'order',
  INCLUDED: 'included',
  COMPLIMENTARY: 'complimentary',
});

const ENTITLEMENT_SOURCE_VALUES = Object.freeze(Object.values(ENTITLEMENT_SOURCE));

const BILLING_CYCLE = Object.freeze({
  MONTHLY: 'monthly',
  ANNUAL: 'annual',
});

const BILLING_CYCLE_VALUES = Object.freeze(Object.values(BILLING_CYCLE));

/** Every app's trial: 14 days, no card, read-only at the end (Q-AE-19). */
const TRIAL_DAYS = 14;

/** Annual billing charges exactly ten months (Q-AE-21 "two months free", Q-AE-24 "exactly 10 × monthly"). */
const ANNUAL_MONTHS_CHARGED = 10;

/** Every MONTHLY unit price ends in .95 (Q-AE-14); the cents-within-the-dollar it must carry. */
const PRICE_CENTS_ENDING = 95;

/**
 * What a dimension's MONTHLY unit price must end in. The owner's price sheet (Q-AE-14) rules ".95" for every price
 * it names in dollars-and-cents AND "$6 per employee paid" in the same breath, so the rule is a property of the
 * dimension: `ninety_five` for every dimension but one; `whole_dollars` for PayDayPuffin's employee paid.
 */
const PRICE_ENDING = Object.freeze({
  NINETY_FIVE: 'ninety_five',
  WHOLE_DOLLARS: 'whole_dollars',
});

const PRICE_ENDING_VALUES = Object.freeze(Object.values(PRICE_ENDING));

module.exports = {
  APP_KIND,
  APP_KIND_VALUES,
  PRICING_MODEL,
  PRICING_MODEL_VALUES,
  DIMENSION_KEY,
  DIMENSION_KEY_VALUES,
  CONSUMABLE_KEY,
  CONSUMABLE_KEY_VALUES,
  QUANTITY_SOURCE,
  QUANTITY_SOURCE_VALUES,
  DIMENSION_KIND,
  DIMENSION_KIND_VALUES,
  CONSUMABLE_SCOPE,
  CONSUMABLE_SCOPE_VALUES,
  ENTITLEMENT_STATUS,
  ENTITLEMENT_STATUS_VALUES,
  HELD_ENTITLEMENT_STATUSES,
  ENTITLEMENT_STATUS_BADGE_TONES,
  ENTITLEMENT_SOURCE,
  ENTITLEMENT_SOURCE_VALUES,
  BILLING_CYCLE,
  BILLING_CYCLE_VALUES,
  TRIAL_DAYS,
  ANNUAL_MONTHS_CHARGED,
  PRICE_CENTS_ENDING,
  PRICE_ENDING,
  PRICE_ENDING_VALUES,
};
